import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import FormSelect from '../../components/Form/FormSelect';
import {
    DESTINATION_CONSTANTS, PRODUCT_TYPE_CONSTANTS,
    PROJECT_TYPE_CONSTANTS,
    SALE_TERRITORY_TYPE_CONSTANTS,
    COUNTRY_CONSTANT, STATE_CONSTANTS
} from './FormConstant';
import FormTextInputRow from '../../components/Form/FormTextInputRow';
import FormCheckbox from '../../components/Form/FormCheckbox';
import FormDatePicker from '../../components/Form/FormDatePicker';

const ShipmentDetailForm = ({data, prevFormData, submitFunction, buttonLabel, isEditable}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [shipmentDetailValues, setShipmentDetailValues] = useState({
        destination: prevFormData?.destination || '',
        address1: prevFormData?.address1 || '',
        address2: prevFormData?.address2 || '',
        city: prevFormData?.city || '',
        state: prevFormData?.state || '',
        postcode: prevFormData?.postcode || '',
        country: prevFormData?.country || '',
        customerName: prevFormData?.customerName || '',
        customerPhone: prevFormData?.customerPhone || '',
        contactName: prevFormData?.contactName || '',
        contactEmail: prevFormData?.contactEmail || '',
        projectType: prevFormData?.projectType || '',
        productLocation: prevFormData?.productLocation || '',
        salesTerritory: prevFormData?.salesTerritory || '',
        territoryEmail: prevFormData?.territoryEmail || '',
        productType: prevFormData?.productType || '',
        shipToSameAddress: prevFormData?.shipToSameAddress || false,
        shipmentDestination: prevFormData?.shipmentDestination || '',
        shipmentAddress: prevFormData?.shipmentAddress || '',
        shipmentAddress2: prevFormData?.shipmentAddress2 || '',
        shipmentCity: prevFormData?.shipmentCity || '',
        shipmentState: prevFormData?.shipmentState || '',
        shipmentPostcode: prevFormData?.shipmentPostcode || '',
        shipmentCountry: prevFormData?.shipmentCountry || '',
        projectName: prevFormData?.projectName || '',
        projectShortName: prevFormData?.projectShortName || '',
        chargeable: prevFormData?.chargeable || false,
        requestedDeliveryDate: prevFormData?.requestedDeliveryDate || '',
        estimatedDeliveryDate: prevFormData?.estimatedDeliveryDate || '',
        ownerName: prevFormData?.ownerName || '',
        ownerPhone: prevFormData?.ownerPhone || '',
        ownerEmail: prevFormData?.ownerEmail || '',
        ShipToCustomerName: prevFormData?.ShipToCustomerName || '',
        shipToCustomerPhone: prevFormData?.shipToCustomerPhone || '',
        shipToContactName: prevFormData?.shipToContactName || '',
        shipToContactEmail: prevFormData?.shipToContactEmail || '',
        note: prevFormData?.note || '',
    });

    const formikShipmentDetailValuesSchema = Yup.object().shape({
        destination: Yup.string(),
        address1: Yup.string().required('Field Required'),
        address2: Yup.string(),
        city: Yup.string().required('Field Required'),
        state: Yup.string().required('Field Required'),
        postcode: Yup.string().required('Field Required'),
        country: Yup.string(),
        customerName: Yup.string().required('Field Required'),
        customerPhone: Yup.string().required('Field Required'),
        contactName: Yup.string().required('Field Required'),
        contactEmail: Yup.string(),
        projectType: Yup.string(),
        salesTerritory: Yup.string().required('Field Required'),
        productType: Yup.string().required('Field Required'),
        productLocation: Yup.string(),
        shipToSameAddress: Yup.boolean().required('Field Required'),
        shipmentDestination: Yup.string().required('Field Required'),
        shipmentAddress: Yup.string().required('Field Required'),
        shipmentAddress2: Yup.string(),
        shipmentCity: Yup.string().required('Field Required'),
        shipmentState: Yup.string().required('Field Required'),
        shipmentPostcode: Yup.string().required('Field Required'),
        shipmentCountry: Yup.string(),
        projectName: Yup.string(),
        projectShortName: Yup.string(),
        chargeable: Yup.boolean(),
        requestedDeliveryDate: Yup.date()
            .test('no-weekends', 'Weekends are not allowed', (value) => {
                if (!value) return true;
                const day = value.getDay();
                return day !== 0 && day !== 6; // Not Sunday or Saturday
            }),
        estimatedDeliveryDate: Yup.date().required('Field Required'),
        ownerName: Yup.string().required('Field Required'),
        ownerPhone: Yup.string().required('Field Required'),
        ownerEmail: Yup.string().required('Field Required'),
        ShipToCustomerName: Yup.string().required('Field Required'),
        shipToCustomerPhone: Yup.string().required('Field Required'),
        shipToContactName: Yup.string().required('Field Required'),
        shipToContactEmail: Yup.string(),
        note: Yup.string(),
    });

    const shipmentDetailsSubmitHandler = async (formData) => {
        if(buttonLabel === 'Create Order' || buttonLabel === 'Add to Order') {
            submitFunction(data,formData)
        } else {
            submitFunction(formData)
        }
    }

    useEffect( () => {
        const newDate = new Date();
        newDate.setDate(newDate.getDate() + 1);

        if(shipmentDetailValues.shipmentDestination !== '' && shipmentDetailValues.shipmentDestination === 'International' && shipmentDetailValues.shipmentCountry !== '') {
            const destinationLeadTime = COUNTRY_CONSTANT.filter(element => element.name === shipmentDetailValues.shipmentCountry);
            newDate.setDate(newDate.getDate() + destinationLeadTime[0].leadtime);
        } else if (shipmentDetailValues.shipmentDestination !== '' && shipmentDetailValues.shipmentDestination === 'Domestic' && shipmentDetailValues.shipmentState !== ''){
            const destinationLeadTime = STATE_CONSTANTS.filter(element => element.name === shipmentDetailValues.shipmentState);
            newDate.setDate(newDate.getDate() + destinationLeadTime[0].leadtime);
        }

        if(shipmentDetailValues.productType !== '') {
            const productLeadTime = PRODUCT_TYPE_CONSTANTS.filter(element => element.name === shipmentDetailValues.productType);
            newDate.setDate(newDate.getDate() + productLeadTime[0].leadtime);
        }

        const newEstimatedDeliveryDate = newDate.toISOString().split('T')[0]

        setShipmentDetailValues({
            ...shipmentDetailValues,
            estimatedDeliveryDate: newEstimatedDeliveryDate,
        })

    }, [shipmentDetailValues.shipmentDestination, shipmentDetailValues.productType, shipmentDetailValues.shipmentCountry, shipmentDetailValues.shipmentState]);

    useEffect( () => {
        if(shipmentDetailValues.shipToSameAddress) {
            setShipmentDetailValues({
                ...shipmentDetailValues,
                shipmentDestination: shipmentDetailValues.destination,
                shipmentAddress: shipmentDetailValues.address1,
                shipmentAddress2: shipmentDetailValues.address2,
                shipmentCity: shipmentDetailValues.city,
                shipmentState: shipmentDetailValues.state,
                shipmentPostcode: shipmentDetailValues.postcode,
                shipmentCountry: shipmentDetailValues.country,
                ShipToCustomerName: shipmentDetailValues.customerName,
                shipToCustomerPhone: shipmentDetailValues.customerPhone,
                shipToContactName: shipmentDetailValues.contactName,
                shipToContactEmail: shipmentDetailValues.contactEmail,
            })
        }
    }, [shipmentDetailValues.shipToSameAddress]);

    useEffect( () => {
        if(shipmentDetailValues.salesTerritory) {
            const saleTerritoryObject = SALE_TERRITORY_TYPE_CONSTANTS.filter(element => element.name === shipmentDetailValues.salesTerritory);
            setShipmentDetailValues({
                ...shipmentDetailValues,
                territoryEmail: saleTerritoryObject[0].email,
            })
        }
    }, [shipmentDetailValues.salesTerritory]);

    return (
        <Formik
            // eslint-disable-next-line react/jsx-boolean-value
            enableReinitialize={true}
            initialValues={shipmentDetailValues}
            validationSchema={formikShipmentDetailValuesSchema}
            onSubmit={shipmentDetailsSubmitHandler}
        >
            {({ values, handleChange, handleSubmit, resetForm }) => {
                // Update state when form values change
                useEffect(() => {
                    setShipmentDetailValues(values);
                }, [values]);

                return (
                    <Form className="flex flex-row w-full h-full gap-2 py-4">
                        <div className="flex flex-col w-1/3 h-full">
                            <h2 className="font-bold">Ordered By:</h2>
                            <FormTextInputRow name="customerName" label='Customer Name:' isDisable={isEditable} />
                            <FormTextInputRow name="customerPhone" label='Customer Phone:' isDisable={isEditable} />
                            <FormTextInputRow name="contactName" label='Contact Name:' isDisable={isEditable} />
                            <FormTextInputRow name="contactEmail" label='Contact Email:' isDisable={isEditable} />
                            <FormSelect
                                name="destination"
                                label='Destination:'
                                placeholder="Destination"
                                options={DESTINATION_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                isDisable={isEditable}
                            />
                            <FormTextInputRow name="address1" label='Address 1:' isDisable={isEditable} />
                            <FormTextInputRow name="address2" label='Address 2:' isDisable={isEditable} />
                            <FormTextInputRow name="city" label='City:' isDisable={isEditable} />
                            {shipmentDetailValues.destination != null && shipmentDetailValues.destination === 'Domestic' &&
                                <FormSelect
                                    name="state"
                                    label='State:'
                                    placeholder="Select State"
                                    options={STATE_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                    isDisable={isEditable}
                                />
                            }
                            {shipmentDetailValues.destination != null && shipmentDetailValues.destination === 'International' &&
                                <FormTextInputRow name="state" label='State:' isDisable={isEditable} />
                            }

                            <FormTextInputRow name="postcode" label='Postcode:' isDisable={isEditable} />
                            {shipmentDetailValues.destination != null && shipmentDetailValues.destination === 'International' &&
                                <FormSelect
                                    name="country"
                                    label='Country:'
                                    placeholder="Select Country"
                                    options={COUNTRY_CONSTANT.map((element) => ({ key: element.id, value: element.name }))}
                                    isDisable={isEditable}
                                />
                            }
                            <FormCheckbox name="shipToSameAddress" label="Ship to Same address ?" isDisable={isEditable}/>
                        </div>
                        <div className="flex flex-col w-1/3 h-full">
                            <h2 className="font-bold">Ship To:</h2>
                            <FormTextInputRow name="ShipToCustomerName" label='Customer Name:' isDisable={isEditable} />
                            <FormTextInputRow name="shipToCustomerPhone" label='Customer Phone:' isDisable={isEditable} />
                            <FormTextInputRow name="shipToContactName" label='Contact Name:' isDisable={isEditable} />
                            <FormTextInputRow name="shipToContactEmail" label='Contact Email:' isDisable={isEditable} />
                            <FormSelect
                                name="shipmentDestination"
                                label='Destination:'
                                placeholder="Select Destination"
                                options={DESTINATION_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                isDisable={isEditable}
                            />
                            <FormTextInputRow name="shipmentAddress" label='Address 1:' isDisable={isEditable} />
                            <FormTextInputRow name="shipmentAddress2" label='Address 2:' isDisable={isEditable} />
                            <FormTextInputRow name="shipmentCity" label='City:' isDisable={isEditable} />
                            {shipmentDetailValues.shipmentDestination != null && shipmentDetailValues.shipmentDestination === 'Domestic' &&
                                <FormSelect
                                    name="shipmentState"
                                    label='State:'
                                    placeholder="Select State"
                                    options={STATE_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                    isDisable={isEditable}
                                />
                            }
                            {shipmentDetailValues.shipmentDestination != null && shipmentDetailValues.shipmentDestination === 'International' &&
                                <FormTextInputRow name="shipmentState" label='State:' isDisable={isEditable} />
                            }
                            <FormTextInputRow name="shipmentPostcode" label='Postcode:' isDisable={isEditable} />
                            {shipmentDetailValues.shipmentDestination != null && shipmentDetailValues.shipmentDestination === 'International' &&
                                <FormSelect
                                    name="shipmentCountry"
                                    label='Shipment Country:'
                                    placeholder="Select Shipment Country"
                                    options={COUNTRY_CONSTANT.map((element) => ({ key: element.id, value: element.name }))}
                                    isDisable={isEditable}
                                />
                            }
                            {/* <FormCheckbox name="chargeable" label="Chargeable ?" /> */}
                            <FormDatePicker name="requestedDeliveryDate" label='Requested Delivery Date:' isDisable={isEditable} />
                        </div>
                        <div className="flex flex-col w-1/3 h-full">
                            <h2 className="font-bold">Project Details:</h2>
                            <FormTextInputRow name="ownerName" label='Owner Name:' isDisable={isEditable} />
                            <FormTextInputRow name="ownerPhone" label='Owner Phone:' isDisable={isEditable} />
                            <FormTextInputRow name="ownerEmail" label='Owner Email:' isDisable={isEditable} />
                            <FormTextInputRow name="projectName" label='Project Name:' isDisable={isEditable} />
                            <FormSelect
                                name="projectType"
                                label='Project Type:'
                                placeholder="Select Project Type"
                                options={PROJECT_TYPE_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                isDisable={isEditable}
                            />
                            <FormTextInputRow name="productLocation" label='Project Location:' isDisable={isEditable} />
                            <FormSelect
                                name="salesTerritory"
                                label='Sale Territory:'
                                placeholder="Select Sale Territory"
                                options={SALE_TERRITORY_TYPE_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                isDisable={isEditable}
                            />
                            <FormTextInputRow name="territoryEmail" label="Territory Email:" isDisable="true" />
                            <FormSelect
                                name="productType"
                                label='Product Type:'
                                placeholder="Select Product Type"
                                options={PRODUCT_TYPE_CONSTANTS.map((element) => ({ key: element.id, value: element.name }))}
                                isDisable={isEditable}
                            />
                            <FormDatePicker name="estimatedDeliveryDate" label='Estimated Delivery Date:' isDisable='true' />
                            <FormTextInputRow name="note" label='Note:' isDisable={isEditable} />

                            {shipmentDetailValues.estimatedDeliveryDate !== "" && shipmentDetailValues.requestedDeliveryDate !== "" && shipmentDetailValues.estimatedDeliveryDate > shipmentDetailValues.requestedDeliveryDate &&
                                <h2 className="font-bold text-red-500">The estimated delivery date is later than the required delivery date. Please be aware that delivery may not meet the required timeline.</h2>
                            }

                            <div className="flex flex-row w-full items-end justify-end mt-4">
                                {!isEditable &&
                                    <button
                                        type="submit"
                                        id='2'
                                        className="w-3/4 p-2 mt-2 mb-2 border-2 border-slate-300 rounded bg-slate-100 text-[11px] text-black font-bold"
                                        disabled={(isLoading || isEditable)}
                                    >
                                        {buttonLabel}
                                    </button>
                                }
                            </div>
                        </div>

                    </Form>
                )}}
        </Formik>
    )
};

ShipmentDetailForm.propTypes = {
    data: PropTypes.object.isRequired,
    prevFormData: PropTypes.object,
    submitFunction: PropTypes.func.isRequired,
    buttonLabel: PropTypes.string,
    isEditable: PropTypes.bool,
};

ShipmentDetailForm.defaultProps = {
    prevFormData: {},
    buttonLabel: 'Create Order',
    isEditable: false,
};

export default ShipmentDetailForm;